import { delay } from "@fxts/core";

import { getDao } from "~/api";
import { disconnectAuth } from "~/modules/Router/Auth/utils/disconnectAuth";
import { store } from "~/store";
import { toSubdomainPolicy } from "~/utils/converter";

const ERROR_PATHNAME = {
  "404": "/error/404",
  "500": "/error/500",
};

const errorHandleForEachStatusCode = async <T>(
  res: Response,
): Promise<T> | never => {
  if (!res.ok) {
    switch (res.status) {
      case 401:
        alert(`The session has ended \nPlease try log in again.`);
        disconnectAuth();
        break;
      case 403:
      case 404: {
        const data = await res.json();
        switch (data.message) {
          case "NO_SUBDOMAIN":
            await replaceNewSubdomainOrErrorPage();
            // res.ok === false 경우 더이상 뒤로 흘러서는 안됨.
            // 지연을 임의로 줘서 그전에 location replace 되도록 수정
            await delay(10000);
            break;
          default:
            location.replace(ERROR_PATHNAME["404"]);
        }
        break;
      }
      case 429:
        alert(
          `Service was temporarily restricted due to abnormal repeated activity detected.\nPlease try again later.`,
        );
        break;
      case 418:
      case 500:
        location.replace(ERROR_PATHNAME["500"]);
        break;
      default:
        return Promise.reject({
          status: res.status,
          statusText: res.statusText,
        });
    }
  }

  return res.json() as Promise<T>;
};

/** @description daoId를 통해 Subdomain 조회 후 있으면 이동 없으면 404 페이지 이동 */
async function replaceNewSubdomainOrErrorPage() {
  const daoId = getCurrentDaoId();
  const { subdomain, pathname } = getCurrentLocationInfo();

  if (daoId) {
    try {
      const { code, data } = await getDao({
        daoId,
      });
      if (code === "000") {
        location.replace(getPathnameForRedirect(data.subdomain));
      } else {
        throw new Error(code);
      }
    } catch (err) {
      location.replace(ERROR_PATHNAME["404"]);
    }
  } else {
    location.replace(ERROR_PATHNAME["404"]);
  }

  function getPathnameForRedirect(nextSubdomain: string) {
    const _nextSubdomain = toSubdomainPolicy(nextSubdomain);

    // 서브도메인이 정상적으로 존재 하는 경우는 현재 페이지를 유지 시키기 위해 변경된 subdomain으로 변경
    if (subdomain) {
      return pathname.replace(subdomain, _nextSubdomain);
    } else {
      // 서브도메인이 정상적으로 존재 하지 않는 경우 커뮤니티 메인으로 이동
      return `/${_nextSubdomain}`;
    }
  }

  function getCurrentLocationInfo() {
    const pathname = location.pathname;
    const subdomain = pathname.split("/")[1];
    const isSubdomain = subdomain.startsWith("@");
    return {
      pathname,
      subdomain: isSubdomain ? subdomain : null,
    };
  }
  function getCurrentDaoId(): string | null {
    const {
      global: {
        global: { userId },
      },
      recentEnter: { daoIds },
    } = store.getState();

    return daoIds[userId ?? ""] ?? null;
  }
}

export default errorHandleForEachStatusCode;
